/**
 * API Endpoints for Thaklis application
 */
import { API_BASE_URL as BASE_URL, API_PATHS } from '../utils/constants';
export const API_VERSION = '/v1';
// Use relative path base for XHR to leverage CRA proxy in dev (avoids CORS)
export const API_BASE_V1 = `${API_VERSION}`;

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_VERSION}/auth/login`,
    REFRESH: `${API_VERSION}/auth/refresh`,
    LOGOUT: `${API_VERSION}/auth/logout`,
  },
  TICKETS: {
    LIST: `${API_VERSION}/tickets`,
    CREATE: `${API_VERSION}/tickets`,
    GET: (id) => `${API_VERSION}/tickets/${id}`,
    UPDATE: (id) => `${API_VERSION}/tickets/${id}`,
    DELETE: (id) => `${API_VERSION}/tickets/${id}`,
    SEARCH: `${API_VERSION}/tickets/search`,
    ASSIGN: (id) => `${API_VERSION}/tickets/${id}/assign`,
    UPDATE_STATUS: (id) => `${API_VERSION}/tickets/${id}/status`,
  },
  USERS: {
    LIST: `${API_VERSION}/users`,
    CREATE: `${API_VERSION}/users`,
    GET: (id) => `${API_VERSION}/users/${id}`,
    UPDATE: (id) => `${API_VERSION}/users/${id}`,
    DELETE: (id) => `${API_VERSION}/users/${id}`,
    SEARCH: `${API_VERSION}/users/search`,
  },
  EMPLOYEES: {
    BASE: `${API_VERSION}/employees`,
    LIST: `${API_VERSION}/employees`,
    CREATE: `${API_VERSION}/employees`,
    GET: (id) => `${API_VERSION}/employees/${id}`,
    UPDATE: (id) => `${API_VERSION}/employees/${id}`,
    DELETE: (id) => `${API_VERSION}/employees/${id}`,
    VALIDATE_DOC_IDS: `${API_VERSION}/employees/validate`,
    SEARCH: `${API_VERSION}/employees/search`,
    SEARCH_FOR_TICKET: `${API_VERSION}/employees/search-for-ticket`,
    UPDATE_UID: (id) => `${API_VERSION}/employees/${id}/uid`,
    DETAIL: (id) => `${API_VERSION}/employees/${id}`,
    ACTIVATE_LOGIN: (id) => `${API_VERSION}/employees/${id}/activate-login`,
    IMPORT: `${API_VERSION}/employees/import`,
    IMPORT_VALIDATED: `${API_VERSION}/employees/import/validated`,
    IMPORT_TEMPLATE: `${API_VERSION}/employees/import/template`,
    IMPORT_PDF: `${API_VERSION}/employees/import/pdf`,
  },
  UID_DETAILS: {
    LIST: `${API_VERSION}/uid-details`,
    GET: (id) => `${API_VERSION}/uid-details/${id}`,
    SEARCH: `${API_VERSION}/uid-details/search`,
  },
  DOCUMENTS: {
    UPLOAD: `${API_VERSION}/documents/upload`,
    GET: (id) => `${API_VERSION}/documents/${id}`,
    DELETE: (id) => `${API_VERSION}/documents/${id}`,
  },
  DOCUMENT_PROCESSING: {
    EXTRACT: `${API_VERSION}/documents/processing/extract`,
    EXTRACT_FROM_PATH: `${API_VERSION}/documents/processing/extract-from-path`,
    TYPES: `${API_VERSION}/documents/processing/types`,
    VALIDATE_PHOTO: `${API_VERSION}/documents/processing/validate-photo`,
    AUTO_FIX_PHOTO: `${API_VERSION}/documents/processing/auto-fix-photo`,
  },
  FILES: {
    UPLOAD: `${API_VERSION}/files/upload`,
    // Use absolute URLs so they render on correct API host even when used in <a href> / <img src>
    DOWNLOAD: (filePath) => `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(filePath)}`,
    DOWNLOAD_BYTES: (filePath) => `${API_PATHS.V1}/files/download/bytes?filePath=${encodeURIComponent(filePath)}`,
  },
  EMPLOYEE_DOCUMENTS: {
    LIST_BY_UID: (uid) => `${API_VERSION}/employee-documents/${encodeURIComponent(uid)}`,
    GROUPED_BY_UID: (uid) => `${API_VERSION}/employee-documents/${encodeURIComponent(uid)}/grouped`,
    BULK: (uid) => `${API_VERSION}/employee-documents/bulk/${encodeURIComponent(uid)}`,
    SET_PRIMARY: (id) => `${API_VERSION}/employee-documents/set-primary/${id}`,
  },
  REPORTS: {
    DOCUMENTS: {
      LIST: `${API_VERSION}/reports-v2/documents`,
      EXPORT: `${API_VERSION}/reports-v2/documents/export`,
      EMAIL: `${API_VERSION}/reports/documents/email`,
      SUMMARY: `${API_VERSION}/reports/documents/summary`,
      TYPES: `${API_VERSION}/reports/documents/types`
    },
    MISSING_DOCUMENTS: {
      LIST: `${API_VERSION}/reports-v2/missing-documents`,
      EXPORT: `${API_VERSION}/reports-v2/missing-documents/export`,
      SUMMARY: `${API_VERSION}/reports-v2/missing-documents/summary`
    },
    STORAGE: {
      LIST: `${API_VERSION}/reports/storage`,
      RECOMPUTE: `${API_VERSION}/reports/storage/recompute`
    }
  },
  ORGANIZATION_DOCUMENTS: {
    GROUPED: (orgId) => `${API_VERSION}/organization-documents/${orgId}/grouped`,
    LIST: (orgId) => `${API_VERSION}/organization-documents/${orgId}`,
    BY_TYPE: (orgId) => `${API_VERSION}/organization-documents/${orgId}/by-type`,
    BULK: (orgId) => `${API_VERSION}/organization-documents/${orgId}/bulk`,
    GET: (id) => `${API_VERSION}/organization-documents/id/${id}`,
    DELETE: (orgId, id) => `${API_VERSION}/organization-documents/${orgId}/id/${id}`,
  },
  ORGANIZATION_DOCUMENT_CATEGORIES: {
    LIST: '/api/organization-document-categories',
  },
  ORGANIZATION_DOCUMENT_TYPES: {
    LIST: '/api/organization-document-types',
    BY_CATEGORY: (categoryId) => `/api/organization-document-types/by-category/${categoryId}`,
  },
  ORG_DETAILS: {
    BY_ORG: (orgId) => `${API_VERSION}/organization-details/${orgId}`,
  },
  ORGANISATIONS: {
    LIST: `${API_VERSION}/organisation`
  },
  PAYMENT_GATEWAYS: {
    LIST: `${API_VERSION}/payment-gateways`,
    ENABLED: `${API_VERSION}/payment-gateways/enabled`,
    GET: (id) => `${API_VERSION}/payment-gateways/${id}`,
    CREATE: `${API_VERSION}/payment-gateways`,
    UPDATE: (id) => `${API_VERSION}/payment-gateways/${id}`,
    TOGGLE_MODE: (id) => `${API_VERSION}/payment-gateways/${id}/toggle-mode`,
    TOGGLE_STATUS: (id) => `${API_VERSION}/payment-gateways/${id}/toggle-status`,
    COST_ANALYSIS: `${API_VERSION}/payment-gateways/cost-analysis`,
    CUSTOMER_CONFIG: {
      LIST: `${API_VERSION}/payment-gateways/customer-config`,
      GET_BY_ORG: (orgId) => `${API_VERSION}/payment-gateways/customer-config/org/${orgId}`,
      ENABLE: `${API_VERSION}/payment-gateways/customer-config/enable`,
      DISABLE: (id) => `${API_VERSION}/payment-gateways/customer-config/${id}/disable`,
    },
  },
  BUSINESS_CATEGORIES: {
    MY_REQUIRED_DOCUMENTS: '/api/business-categories/my-required-documents',
  },
  TOPUP: {
    LIST: `${API_VERSION}/topup`,
    CREATE: `${API_VERSION}/topup`,
    GET: (id) => `${API_VERSION}/topup/${id}`,
    APPROVE: (id) => `${API_VERSION}/topup/${id}/approve`,
    REJECT: (id) => `${API_VERSION}/topup/${id}/reject`,
    MY_REQUESTS: `${API_VERSION}/topup/my-requests`,
  }
};

export default API_ENDPOINTS;
